import { Layout } from "@/components/layout/layout";
import { useListTournaments, useGetTournamentResults, getGetTournamentResultsQueryKey } from "@workspace/api-client-react";
import { formatDateTime, formatMoney } from "@/lib/format";
import { useSeo } from "@/lib/seo";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, ListOrdered } from "lucide-react";
import { Link } from "wouter";
import { Skeleton } from "@/components/ui/skeleton";
import { ListEmpty, ListErrorBlock } from "@/components/list-state";

function TournamentResultsGroup({ tournamentId, tournament }: { tournamentId: number, tournament: any }) {
  const { data: results, isLoading } = useGetTournamentResults(tournamentId, {
    query: { enabled: !!tournamentId, queryKey: getGetTournamentResultsQueryKey(tournamentId) }
  });

  if (isLoading) return <div className="p-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  if (!results || results.length === 0) return null;

  return (
    <Card className="bg-background border-border overflow-hidden">
      <CardHeader className="bg-muted/30 border-b border-border flex flex-row items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <CardTitle className="text-lg sm:text-xl hover:text-primary transition-colors break-words">
            <Link href={`/tournaments/${tournament.id}`}>{tournament.name}</Link>
          </CardTitle>
          <div className="text-xs sm:text-sm text-muted-foreground mt-1 break-words">
            {formatDateTime(tournament.date)} • {tournament.leagueName}
          </div>
        </div>
        <Badge variant="outline" className="shrink-0">{tournament.entries} Entries</Badge>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y divide-border">
          {results.slice(0, 5).map((result) => (
            <div key={result.id} className="flex items-center gap-3 p-3 sm:p-4 hover:bg-muted/10">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm shrink-0
                ${result.position === 1 ? 'bg-yellow-500/20 text-yellow-600' :
                  result.position === 2 ? 'bg-gray-400/20 text-gray-400' :
                  result.position === 3 ? 'bg-amber-700/20 text-amber-700' :
                  'bg-card text-muted-foreground border border-border'}`}>
                {result.position}
              </div>
              <span className="font-medium text-foreground min-w-0 flex-1 break-words text-sm sm:text-base">
                {result.playerName}
              </span>
              <div className="flex flex-col sm:flex-row sm:items-center sm:gap-6 text-sm shrink-0 text-right">
                <span className="text-primary font-bold tabular-nums">{result.points} pts</span>
                <span className="font-medium tabular-nums sm:w-20">{formatMoney(result.winnings)}</span>
              </div>
            </div>
          ))}
        </div>
        {results.length > 5 && (
          <div className="p-3 text-center bg-muted/10 border-t border-border text-sm text-muted-foreground">
            +{results.length - 5} more finishers
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function Results() {
  useSeo({
    title: "Recent Tournament Results",
    description:
      "See the latest finishing positions, payouts, and knockouts from Interclub Poker Circuit tournaments across every venue.",
    canonicalPath: "/results",
  });
  const { data: tournamentsPage, isLoading, isError, error, refetch } = useListTournaments({ status: "completed", limit: 200 });
  const tournaments = tournamentsPage?.items;

  const sortedTournaments = [...(tournaments ?? [])].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  return (
    <Layout>
      <div className="bg-card border-b border-border py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold text-foreground tracking-tight">Recent Results</h1>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl">
            Latest payouts and points awarded across the circuit.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {isLoading ? (
          <div className="space-y-6 max-w-4xl mx-auto">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-56 w-full rounded-xl" />
            ))}
          </div>
        ) : isError ? (
          <ListErrorBlock error={error} onRetry={() => refetch()} title="Couldn't load results" />
        ) : sortedTournaments.length === 0 ? (
          <ListEmpty
            icon={<ListOrdered className="h-7 w-7" />}
            title="No completed tournaments yet"
            description="Recent finishes and payouts will appear here once tournaments wrap up."
          />
        ) : (
          <div className="space-y-8 max-w-4xl mx-auto">
            {sortedTournaments.map(t => (
              <TournamentResultsGroup key={t.id} tournamentId={t.id} tournament={t} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
